import { App } from 'BMapsSrc/BMapsApp';
import { UserActions } from 'BMapsCmds';
import { ensureArray } from 'BMapsUtil/js_utils';
import { BindingSite, Compound } from 'BMapsModel';
import { makeOneSDF, molBlockForAtoms } from 'BMapsSrc/util/mol_format_utils';
import { createJob, loadResult } from 'BMapsRedux/jobs/access';
import { FepScoringJob } from 'BMapsSrc/jobman/jobs/FepScoringJob';
import { defaultTarget, targetOptions, targetProblem } from 'BMapsSrc/integrations/compute_targets';

/**
 * The FEP programs BMaps can submit. Keys are the cpaws `--program` values;
 * the jobtype (underscored) is the wire contract that must match the
 * backend's `get_details()` keys.
 *
 * - relative (RBFE): a network of ligand pairs; per-ligand ΔG comes from a
 *   maximum-likelihood solve over the ΔΔG edges, so it needs several ligands.
 * - absolute (ABFE): one calculation per ligand, no network; the only option
 *   for a single compound. OpenFE only.
 *
 * GROMACS RBFE (pmx + alchemlyb, /opt/gromacs/rbfe) runs on ConiferGPU2 only
 * for now (compute_targets.js), takes one receptor chain and protein only (no
 * ions or cofactors), and solves its own network MLE, so 2 ligands already
 * give a ΔG (`minCompoundsForDg`). TMD's network MLE (cpaws jobrun_tmd.py,
 * tmd.fe.mle) also works from one edge, so TMD needs 2 as well. Only OpenFE
 * RBFE needs 4 (its MLE refuses fewer than 3 edges: MIN_COMPOUNDS_FOR_DG).
 */
// The jobtypes are literals rather than `JobTypes.*` from the job registry:
// the registry imports the FEP job class, whose import chain reaches this
// module, so the enum is not initialised yet when this table is built.
// `FepScoringJob.test.js` asserts the literals equal the registry's enum.
export const FEP_PROGRAMS = {
    'openfe-rbfe': {
        key: 'openfe-rbfe',
        jobtype: 'openfe_rbfe',
        engine: 'openfe',
        calculation: 'rbfe',
        label: 'OpenFE relative (RBFE)',
        minCompoundsForDg: 4,
    },
    'openfe-abfe': {
        key: 'openfe-abfe', jobtype: 'openfe_abfe', engine: 'openfe', calculation: 'abfe', label: 'OpenFE absolute (ABFE)',
    },
    'tmd-rbfe': {
        key: 'tmd-rbfe',
        jobtype: 'tmd_rbfe',
        engine: 'tmd',
        calculation: 'rbfe',
        label: 'TMD relative (RBFE)',
        minCompoundsForDg: 2,
    },
    'gromacs-rbfe': {
        key: 'gromacs-rbfe',
        jobtype: 'gromacs_rbfe',
        engine: 'gromacs',
        calculation: 'rbfe',
        label: 'GROMACS relative (RBFE)',
        minCompoundsForDg: 2,
        maxCompounds: 16,
        singleChain: true,
        proteinOnly: true,
    },
};

export const FEP_ENGINES = [
    { key: 'openfe', label: 'OpenFE' },
    { key: 'tmd', label: 'TMD (timemachine)' },
    { key: 'gromacs', label: 'GROMACS (pmx + alchemlyb)' },
];

export const FEP_CALCULATIONS = [
    { key: 'rbfe', label: 'Relative binding free energy (RBFE)' },
    { key: 'abfe', label: 'Absolute binding free energy (ABFE)' },
];

/** Where the campaign computes. The worker on each queue decides the details. */
export const FEP_TARGETS = [
    { key: 'cpaws', label: 'AWS GPU fleet (cpaws)' },
    { key: 'conifergpu2', label: 'ConiferGPU2 (in-house GPU server)' },
];
const FEP_TARGET_ORDER = FEP_TARGETS.map((t) => t.key);

/**
 * The compute choices for a program, those that cannot run it disabled
 * (compute_targets.js is the table).
 * @param {string} program - a {@link FEP_PROGRAMS} key
 * @returns {{ key: string, label: string, disabled: boolean }[]}
 */
export function fepTargets(program) {
    return targetOptions(FEP_PROGRAMS[program]?.jobtype, FEP_TARGET_ORDER);
}

/**
 * `preferred` if it runs the program, else the first target that does.
 * @param {string} program
 * @param {string} [preferred]
 * @returns {string|undefined}
 */
export function fepDefaultTarget(program, preferred) {
    return defaultTarget(FEP_PROGRAMS[program]?.jobtype, preferred, FEP_TARGET_ORDER);
}

/** GROMACS RBFE network layouts (OpenFE's planner, run by plan_network.py). */
export const GROMACS_NETWORK_LAYOUTS = [
    { key: 'mst', label: 'Minimal spanning tree (OpenFE default)' },
    { key: 'redundant', label: 'Redundant (adds cycles; a failed edge does not cut a ligand off)' },
    { key: 'radial', label: 'Radial (every ligand to one central ligand)' },
];

/**
 * OpenFE ABFE "peptide" sampling profile, for large flexible ligands (the
 * defaults are tuned for ligands of roughly 20-50 heavy atoms). From a pilot on
 * a 99-heavy-atom, 181-atom peptide (bazaar job 1463, 2026-10-20): the default
 * 14-window solvent schedule left neighbouring windows with essentially no
 * overlap (steps of 0.25 electrostatic / 0.12 sterics against about 0.07 / 0.04
 * needed), so MBAR could not converge. A 41-window grid gave neighbour overlaps
 * of 0.13-0.31, apart from one dip at sterics 0.77-0.81.
 *
 * The profile: solvent leg 45 windows (electrostatics, then sterics, denser
 * around sterics 0.74-0.80) and complex leg 46 (fitted to a 0.15 neighbour overlap
 * from a 50-state pilot), production length unchanged at 10 ns. Both legs come
 * from short pilots on a cyclic peptide (2026-10-23; cpaws PR #158); the pilots
 * are in /data/jlkjr/peptide-pilot/short01 on ConiferGPU2. Applied by the cpaws
 * planner (`--mode peptide`), so it is
 * available per compute target, once that target's cpaws has it:
 * ConiferGPU2's /opt/cpawsV2 has it since 2026-10-21 (planning checked on the
 * real inputs, 64 GB leaf cap); the fleet's shared-storage
 * (EFS) deployments do not yet. Flip the fleet's entry when cpaws reports it.
 */
export const ABFE_PEPTIDE_TARGETS = { conifergpu2: true, cpaws: false };
export const ABFE_PEPTIDE_NOT_YET = 'needs cpaws planner support (not available yet)';

/**
 * Whether `target` can plan the peptide profile.
 * @param {string} [target]
 * @returns {boolean}
 */
export function peptideAvailable(target) {
    return ABFE_PEPTIDE_TARGETS[target] === true;
}
/**
 * Lambda windows per leg an ABFE mode may be resampled to (as the backend). At most 55: one
 * OpenMM process holds about 61 CUDA contexts and OpenFE's replica exchange makes one per
 * window, so a 60-window leg failed on ConiferGPU2 when sampling started.
 */
export const ABFE_WINDOWS_LIMITS = { min: 10, max: 55 };

/**
 * Why these ABFE window counts cannot be used, or null.
 * @param {string|number} solvent
 * @param {string|number} complex
 * @returns {string|null}
 */
export function abfeWindowsProblem(solvent, complex) {
    const { min, max } = ABFE_WINDOWS_LIMITS;
    for (const [name, value] of [['Solvent', solvent], ['Complex', complex]]) {
        const n = Number(value);
        if (value === '' || value == null || !Number.isInteger(n) || n < min || n > max) {
            return `${name} windows must be a whole number from ${min} to ${max}`;
        }
    }
    return null;
}

/** GPU-hours per ligand of the 14 + 30 window schedule, by mode (peptide: as production). */
const ABFE_BASE_GPU_HOURS = { production: [30, 45], fast: [6, 9], peptide: [30, 45] };

/**
 * GPU-hours per ligand at these window counts: the mode's cost for the default
 * 14 + 30 windows scaled by the total windows.
 * @param {number} solvent
 * @param {number} complex
 * @param {string} [mode] - 'production' (default) | 'fast' | 'peptide'
 * @returns {[number, number]}
 */
export function peptideGpuHours(solvent, complex, mode = 'production') {
    const scale = (Number(solvent) + Number(complex)) / 44;
    const [low, high] = ABFE_BASE_GPU_HOURS[mode] || ABFE_BASE_GPU_HOURS.production;
    return [Math.round(low * scale), Math.round(high * scale)];
}

/** Above this many heavy atoms the default ABFE windows are unlikely to overlap. */
export const ABFE_PEPTIDE_HEAVY_ATOMS = 60;
export const ABFE_PEPTIDE_PROFILE = {
    solventWindows: 45, complexWindows: 46, productionNs: 10, gpuHoursFactor: 2,
};

/** Lambda windows per leg each ABFE sampling mode plans by default. */
export const ABFE_DEFAULT_WINDOWS = {
    production: { solvent: 14, complex: 30 },
    fast: { solvent: 14, complex: 30 },
    peptide: {
        solvent: ABFE_PEPTIDE_PROFILE.solventWindows, complex: ABFE_PEPTIDE_PROFILE.complexWindows,
    },
};

/**
 * @param {string} [mode] - 'production' | 'fast' | 'peptide'
 * @returns {{ solvent: number, complex: number }}
 */
export function abfeDefaultWindows(mode) {
    return ABFE_DEFAULT_WINDOWS[mode] || ABFE_DEFAULT_WINDOWS.production;
}

/**
 * Whether `windows` ask for other window counts than `mode` plans by default.
 * Only then are they sent: the default request keeps the validated schedules.
 * @param {string} mode
 * @param {{ solvent: number|string, complex: number|string }} [windows]
 * @returns {boolean}
 */
export function abfeWindowsCustom(mode, windows) {
    if (!windows) return false;
    const defaults = abfeDefaultWindows(mode);
    return Number(windows.solvent) !== defaults.solvent
        || Number(windows.complex) !== defaults.complex;
}

/**
 * The OpenFE sampling choices for a form. RBFE has production and fast; ABFE
 * adds the experimental peptide profile (disabled where `target` cannot plan it).
 * @param {boolean} abfe
 * @param {string} [target] - 'conifergpu2' | 'cpaws'
 * @returns {{ key: string, label: string, disabled: boolean }[]}
 */
export function openfeModes(abfe, target) {
    const modes = [
        { key: 'production', label: 'Production', disabled: false },
        { key: 'fast', label: 'Fast', disabled: false },
    ];
    if (abfe) {
        modes.push({
            key: 'peptide',
            label: peptideAvailable(target)
                ? 'Peptide (experimental)' : `Peptide (experimental): ${ABFE_PEPTIDE_NOT_YET}`,
            disabled: !peptideAvailable(target),
        });
    }
    return modes;
}

/**
 * Why `mode` cannot be used for `program`, or null.
 * @param {string} program - a {@link FEP_PROGRAMS} key
 * @param {string} mode
 * @param {string} [target] - 'conifergpu2' | 'cpaws'
 * @returns {string|null}
 */
export function openfeModeProblem(program, mode, target) {
    if (mode !== 'peptide') return null;
    if (FEP_PROGRAMS[program]?.key !== 'openfe-abfe') {
        return 'The peptide sampling profile is for absolute (ABFE) calculations only.';
    }
    return peptideAvailable(target)
        ? null
        : `The peptide sampling profile is not available yet on ${target} (its cpaws planner lacks it).`;
}

/**
 * Compounds big enough that the default ABFE windows are unlikely to overlap.
 * @param {{ resSpec: string, getMolProp?: Function }[]} compounds
 * @param {number} heavyAtomsOf - reads a compound's heavy-atom count
 * @returns {string[]} their names
 */
export function largeAbfeLigands(compounds, heavyAtomsOf) {
    return (compounds || [])
        .filter((c) => heavyAtomsOf(c) > ABFE_PEPTIDE_HEAVY_ATOMS)
        .map((c) => c.resSpec);
}

/**
 * GROMACS RBFE atom mapping per edge. pmx (the default) is the validated one;
 * Kartograf feeds OpenFE's mapping to pmx and is experimental: it fixed one of
 * the two Tyk2 edges compared, so it stays opt-in until more edges are tested.
 */
export const GROMACS_MAPPINGS = [
    { key: 'pmx', label: 'pmx (validated)' },
    { key: 'kartograf', label: 'Kartograf (experimental)' },
];

/**
 * GROMACS RBFE sampling: production is the protocol validated on Tyk2
 * (/opt/gromacs/rbfe/README.md); hours per edge measured on ConiferGPU2,
 * where edges run one at a time.
 */
export const GROMACS_RBFE_MODES = {
    production: { repeats: 3, nsPerWindow: 3, hoursPerEdge: 3.7 },
    fast: { repeats: 2, nsPerWindow: 1, hoursPerEdge: 1.3 },
};

/**
 * About how many edges OpenFE's planner makes for `n` ligands.
 * @param {number} n
 * @param {string} layout - a GROMACS_NETWORK_LAYOUTS key
 * @returns {number}
 */
export function estimatedEdges(n, layout='mst') {
    if (n < 2) return 0;
    if (layout === 'redundant') return Math.min((n * (n - 1)) / 2, 2 * (n - 1));
    return n - 1;
}

/** TMD edge-network policies (OpenFE plans its own network). */
export const TMD_NETWORK_POLICIES = [
    { key: 'star', label: 'Star (every ligand to one reference)' },
    { key: 'chain', label: 'Chain (consecutive pairs)' },
    { key: 'complete', label: 'Complete (all pairs)' },
];

/**
 * Fewest compounds that can produce a per-compound ΔG from a RELATIVE run.
 *
 * A minimal spanning network over N ligands has N-1 edges, and OpenFE's MLE
 * solve refuses a network with fewer than 3 edges ("The results network has N
 * edge(s), but 3 or more edges are required to calculate DG values"). So below
 * 4 compounds a campaign still runs and still yields the pairwise ΔΔG table,
 * but no ΔG is produced and nothing lands on the compounds.
 *
 * Verified on real cpaws runs 2026-08-02: 3 compounds -> 2 edges -> no ΔG;
 * 4 compounds -> 3 edges -> ΔG produced.
 *
 * OpenFE RBFE only: the form and buildFepRequest refuse fewer. GROMACS and TMD
 * RBFE solve their own network MLE and need 2 (`minCompoundsForDg`). Absolute
 * (ABFE) runs are per-ligand and have no such limit.
 */
export const MIN_COMPOUNDS_FOR_DG = 4;

/**
 * Why a selection of this size will not produce per-compound ΔG, or null if it
 * will. The form refuses to submit while this returns a message, so hours of
 * GPU time are never spent on a job whose scores could not reach the workspace.
 * @param {number} count - number of compounds selected
 * @param {string} [program] - FEP program key; absolute runs never fall short
 * @returns {string|null}
 */
export function fepDgShortfall(count, program='openfe-rbfe') {
    const spec = FEP_PROGRAMS[program];
    if (spec?.calculation === 'abfe') return null;
    if (spec?.maxCompounds && count > spec.maxCompounds) {
        return `${count} compounds is more than the ${spec.maxCompounds} one ${spec.label} job may run.`;
    }
    const min = spec?.minCompoundsForDg || MIN_COMPOUNDS_FOR_DG;
    if (count >= min) return null;
    const have = count === 1 ? '1 compound' : `${count} compounds`;
    if (min < MIN_COMPOUNDS_FOR_DG) {
        return `At least ${min} compounds are required for a relative free-energy calculation. `
            + 'Select more compounds, or run one compound as an absolute calculation (OpenFE).';
    }
    return `At least ${MIN_COMPOUNDS_FOR_DG} compounds are required for a relative free-energy `
        + `calculation: the per-compound ΔG needs a network of 3 or more edges. With ${have} `
        + 'only pairwise ΔΔG values could be produced and no ΔG could be applied. '
        + 'Select more compounds, or run one compound as an absolute calculation.';
}

/**
 * Decide which FEP program a request runs, enforcing the one hard rule: a
 * single compound is an absolute (ABFE) calculation, never relative.
 *
 * Either `program` (a {@link FEP_PROGRAMS} key) or `engine` + `calculation`
 * may be given; with neither, one compound defaults to OpenFE ABFE and more
 * default to OpenFE RBFE.
 *
 * @param {{ count: number, program?: string, engine?: 'openfe'|'tmd',
 *   calculation?: 'rbfe'|'abfe' }} spec
 * @returns {{ program?: string, jobtype?: string, engine?: string,
 *   calculation?: string, error?: string }}
 */
export function resolveFepProgram({
    count, program, engine, calculation,
}={}) {
    const n = Number(count) || 0;
    if (n < 1) return { error: 'Select at least one compound.' };
    let key = program;
    if (!key) {
        const eng = engine || 'openfe';
        const calc = calculation || (n === 1 ? 'abfe' : 'rbfe');
        key = `${eng}-${calc}`;
    }
    const spec = FEP_PROGRAMS[key];
    if (!spec) {
        if (String(key).startsWith('tmd') && String(key).endsWith('abfe')) {
            return { error: 'TMD supports relative (RBFE) calculations only; use OpenFE for absolute binding free energy.' };
        }
        if (String(key).startsWith('gromacs') && String(key).endsWith('abfe')) {
            return { error: 'GROMACS supports relative (RBFE) calculations only; use OpenFE for absolute binding free energy.' };
        }
        return { error: `Unknown FEP program: ${key}. Use ${Object.keys(FEP_PROGRAMS).join(', ')}.` };
    }
    if (n === 1 && spec.calculation !== 'abfe') {
        return { error: 'A single compound runs as absolute binding free energy (OpenFE ABFE); relative FEP needs at least 2 compounds.' };
    }
    return {
        program: spec.key,
        jobtype: spec.jobtype,
        engine: spec.engine,
        calculation: spec.calculation,
    };
}

/**
 * The Free Energy form's initial knob values, with anything the caller
 * pre-filled applied on top.
 *
 * This is the pre-fill seam for the Gibbs handoff: rather than submitting a
 * campaign blind from chat, Gibbs collects the chemistry and opens the form
 * with those choices already made, for a chemist to review and submit. It
 * mirrors the screening handoff (`openScreeningSidePanel` with a pre-selected
 * library) that is the approved pattern for a chemistry decision.
 *
 * Every pre-filled value is validated against the real option list and falls
 * back to the form's own default when it is not a legal key, so a wrong guess
 * from the LLM lands the form on its defaults instead of in a state the user
 * cannot submit or the request builder cannot resolve.
 *
 * Pure, so the seeding is testable without rendering the panel.
 *
 * NOTE: receptor chains, ions and cofactors are deliberately NOT seeded here.
 * The panel derives them from the ligands' structure and resets them in an
 * effect keyed on the structure (`setChainIds(allChainIds)`), which would
 * immediately overwrite anything seeded — so a "pre-filled chain selection"
 * would be a value that silently disappears. Those stay the panel's own.
 *
 * @param {object} [initData] - the side panel's initData
 * @param {number} [count] - number of selected ligands (decides the relative /
 *   absolute default, exactly as the form does)
 * @returns {{ engine: string, calculation: string, target: string, mode: string,
 *   runOptimization: string, networkPolicy: string, jobName: string }}
 */
export function fepFormDefaults(initData={}, count=0) {
    const data = initData || {};
    const pick = (value, allowed, fallback) => (
        allowed.includes(value) ? value : fallback
    );
    const calculation = pick(
        data.calculation, FEP_CALCULATIONS.map((c) => c.key),
        count === 1 ? 'abfe' : 'rbfe',
    );
    let engine = pick(data.engine, FEP_ENGINES.map((e) => e.key), 'openfe');
    // An engine and a calculation can each be legal on its own and still not
    // exist as a PAIR: FEP_PROGRAMS holds openfe-rbfe, openfe-abfe and
    // tmd-rbfe, so TMD + absolute is not a program at all. Picking the two
    // fields independently seeded the form with engine: tmd for an absolute
    // run; the component then silently corrected it through
    // `effectiveEngine`, so a caller narrating this return announced an engine
    // the campaign would not use. Resolve the pair here instead.
    //
    // The calculation wins because it is the stronger intent -- it is the
    // question the chemist is asking (per-ligand ΔG or a ΔΔG network), and a
    // single compound can only ever be absolute. Derived from FEP_PROGRAMS
    // rather than hardcoded, so a fourth program needs no change here.
    if (!FEP_PROGRAMS[`${engine}-${calculation}`]) {
        const viable = Object.values(FEP_PROGRAMS)
            .find((spec) => spec.calculation === calculation);
        if (viable) engine = viable.engine;
    }
    return {
        engine,
        calculation,
        target: pick(data.target, FEP_TARGETS.map((t) => t.key), FEP_TARGETS[0].key),
        mode: pick(data.mode, ['production', 'fast'], 'production'),
        runOptimization: pick(data.runOptimization, ['cost', 'time'], 'cost'),
        networkPolicy: pick(
            data.networkPolicy, TMD_NETWORK_POLICIES.map((p) => p.key), 'star',
        ),
        jobName: typeof data.jobName === 'string' ? data.jobName : '',
    };
}

/**
 * @typedef {object} FepNetworkSpec
 * @property {'star'|'chain'|'complete'} [policy] - how ligand pairs become RBFE
 *   edges (TMD only; OpenFE runs its own planner). Default 'star'.
 * @property {string} [reference] - reference ligand name for a star network.
 * @property {'mst'|'redundant'|'radial'} [layout] - GROMACS only: the OpenFE
 *   planner's layout. Default 'mst'.
 * @property {string} [central] - GROMACS radial layout: the central ligand's
 *   name (default the first ligand).
 * @property {'pmx'|'kartograf'} [mapping] - GROMACS only: the atom mapping per
 *   edge. Default 'pmx'.
 */

/**
 * Assemble the job request the backend FEP workflow consumes. Pure, so the
 * payload shape is unit-testable without a workspace.
 * @param {object} p
 * @param {string} p.pdb - receptor PDB text
 * @param {string} p.ligandSdf - ligands, titles = compound resSpec
 * @param {string} [p.cofactorSdf] - cofactors (OpenFE only)
 * @param {string} p.program - a {@link FEP_PROGRAMS} key
 * @param {'cpaws'|'conifergpu2'} [p.target]
 * @param {'production'|'fast'|'peptide'} [p.mode] - OpenFE / GROMACS sampling profile
 * @param {'cost'|'time'} [p.runOptimization] - spot vs on-demand (cpaws)
 * @param {FepNetworkSpec} [p.network] - TMD and GROMACS
 * @param {{ solvent: number, complex: number }} [p.abfeWindows] - ABFE only: lambda
 *   windows per leg; sent only when they differ from the mode's default (14 / 30, peptide 45 / 46)
 * @param {string[]} [p.extraSamplingResidues] - TMD only, "CHAIN:RESNUM"
 * @param {number} [p.maxRuntimeMinutes] - may only lower the worker's cap
 * @param {string} [p.jobName]
 * @param {number} p.ligandCount
 * @returns {object} requestData
 */
export function buildFepRequest({
    pdb, ligandSdf, cofactorSdf, program, target='cpaws', mode='production',
    runOptimization='cost', network, extraSamplingResidues, maxRuntimeMinutes,
    jobName, ligandCount, receptorPrep, abfeWindows,
}) {
    const spec = FEP_PROGRAMS[program];
    const params = { run_optimization: runOptimization, target };
    if (spec.engine === 'openfe' || spec.engine === 'gromacs') params.mode = mode;
    if (spec.key === 'openfe-abfe' && abfeWindowsCustom(mode, abfeWindows)) {
        params.abfe_windows = {
            solvent: Number(abfeWindows.solvent), complex: Number(abfeWindows.complex),
        };
    }
    if (spec.engine === 'gromacs') {
        params.network = { layout: network?.layout || 'mst' };
        params.mapping = network?.mapping || 'pmx';
        if (params.network.layout === 'radial' && network?.central) {
            params.network.central = network.central;
        }
    }
    if (maxRuntimeMinutes > 0) params.max_runtime = Math.round(maxRuntimeMinutes);
    // How the worker prepares the receptor for OpenMM: 'termini' repairs the
    // chain ends and disulfides and keeps BMaps' protonation (the worker's
    // default); 'full' re-places every hydrogen with PDBFixer at pH 7.
    if (receptorPrep) params.receptor_prep = receptorPrep;
    const requestData = {
        receptor_pdb: pdb,
        ligand_sdf: ligandSdf,
        program,
        params,
        ligand_count: ligandCount,
        jobName: jobName || `${program} (${ligandCount} ligand${ligandCount === 1 ? '' : 's'})`,
    };
    if (cofactorSdf) requestData.cofactor_sdf = cofactorSdf;
    if (spec.engine === 'tmd') {
        if (network) requestData.network = network;
        const residues = ensureArray(extraSamplingResidues || [])
            .map((r) => String(r).trim())
            .filter(Boolean);
        if (residues.length > 0) requestData.tmd = { extra_sampling_residues: residues };
    }
    return requestData;
}

/**
 * Submit an FEP (binding free energy) job for a set of compounds.
 *
 * The compute runs on the cpaws GPU fleet or the in-house GPU box (OpenFE or
 * TMD); this just stages the request as a persistent jobman job. The job
 * appears in the Free Energy panel and, when complete, its per-compound ΔG can
 * be viewed there and applied to the workspace compounds. It is NOT awaited
 * here: FEP campaigns run for hours.
 *
 * A single compound always runs as absolute binding free energy (OpenFE ABFE).
 *
 * @param {import('BMapsModel').Compound|import('BMapsModel').Compound[]} compoundsIn
 * @param {object} [opts]
 * @param {'openfe-rbfe'|'openfe-abfe'|'tmd-rbfe'|'gromacs-rbfe'} [opts.program] - explicit
 *   program; or give `engine` and/or `calculation` instead.
 * @param {'openfe'|'tmd'|'gromacs'} [opts.engine]
 * @param {'rbfe'|'abfe'} [opts.calculation]
 * @param {'cpaws'|'conifergpu2'} [opts.target] - AWS fleet (the default where it
 *   runs the program) or ConiferGPU2 (the default for GROMACS RBFE).
 * @param {'production'|'fast'|'peptide'} [opts.mode] - OpenFE / GROMACS sampling profile
 *   ('peptide': OpenFE ABFE only).
 * @param {'cost'|'time'} [opts.runOptimization] - spot (cost) vs on-demand (time).
 * @param {FepNetworkSpec} [opts.network] - TMD edge-network policy / GROMACS layout.
 * @param {{ solvent: number, complex: number }} [opts.abfeWindows] - ABFE only: lambda
 *   windows per leg (10-55); the mode's default (14 / 30, peptide 45 / 46) when omitted.
 * @param {string[]} [opts.extraSamplingResidues] - TMD only: residues
 *   ("CHAIN:RESNUM") given extra sampling in the complex leg.
 * @param {import('BMapsModel').Compound[]} [opts.cofactors] - workspace
 *   compounds sent as cofactors (OpenFE only); must not overlap the ligands.
 * @param {number} [opts.maxRuntimeMinutes] - cap on the campaign's time on the
 *   compute; may only LOWER the worker's configured cap (default 48 h).
 * @param {string} [opts.jobName] - human label shown in the jobs list.
 * @returns {Promise<import('BMapsRedux/jobs/access').createJobResponse & { program?: string }>}
 *   `{ results: { jobid, ... }, error, program }`.
 */
export async function startFepScoring(compoundsIn, {
    program: programIn, engine, calculation, target: targetIn,
    mode = 'production', runOptimization = 'cost', network, extraSamplingResidues,
    cofactors: cofactorsIn, chains, ions: ionsIn, maxRuntimeMinutes, jobName,
    receptorPrep, acknowledgeDgShortfall, abfeWindows,
} = {}) {
    const compounds = ensureArray(compoundsIn);
    const resolved = resolveFepProgram({
        count: compounds.length, program: programIn, engine, calculation,
    });
    if (resolved.error) return { error: resolved.error };
    const { program, jobtype } = resolved;
    const spec = FEP_PROGRAMS[program];
    const target = targetIn || fepDefaultTarget(program, 'cpaws');
    const targetError = targetProblem(jobtype, target);
    if (targetError) return { error: targetError };
    const modeError = openfeModeProblem(program, mode, target);
    if (modeError) return { error: modeError };
    if (abfeWindows && spec.key === 'openfe-abfe') {
        const windowsError = abfeWindowsProblem(abfeWindows.solvent, abfeWindows.complex);
        if (windowsError) return { error: windowsError };
    }
    // Too few (OpenFE RBFE: 4, its MLE needs 3 edges) or too many compounds.
    const sizeProblem = fepDgShortfall(compounds.length, program);
    if (sizeProblem) return { error: sizeProblem };
    const cofactors = ensureArray(cofactorsIn || []);
    const overlap = cofactors.filter((c) => compounds.includes(c));
    if (overlap.length > 0) {
        return { error: `Cofactors must not also be ligands: ${overlap.map((c) => c.resSpec).join(', ')}` };
    }
    if (cofactors.length > 0 && spec.engine === 'tmd') {
        return { error: 'TMD does not accept cofactors; use OpenFE or remove the cofactors.' };
    }

    const ions = ensureArray(ionsIn || []);
    if (spec.proteinOnly && (cofactors.length > 0 || ions.length > 0)) {
        return { error: `${spec.label} simulates the protein only; leave out the ions and cofactors (or use OpenFE).` };
    }
    if (chains && chains.length === 0) {
        return { error: 'Select at least one receptor chain.' };
    }
    if (spec.singleChain && chains && chains.length > 1) {
        return { error: `${spec.label} takes one receptor chain: select the chain the ligands bind.` };
    }

    // Too few ligands for a per-compound ΔG. The Free Energy form has always
    // checked this and blocks Submit (NewFreeEnergyJob.jsx), but every other
    // caller — Gibbs among them — reached createJob without consulting it, so a
    // chat request could spend GPU hours on a campaign whose ΔG can never land.
    // Checked HERE, against the same `fepDgShortfall` predicate the form uses,
    // so there is one source of truth for the rule rather than two.
    //
    // NOT a refusal: `MIN_COMPOUNDS_FOR_DG` is documented above as a warning
    // threshold, not a hard floor, and a relative run on two congeners still
    // yields the pairwise ΔΔG — which is a legitimate medchem answer, just not
    // a per-ligand ΔG. So the caller may proceed by acknowledging it. ABFE is
    // per-ligand and has no floor; `fepDgShortfall` already returns null there.
    //
    // Placed before `App.partitionByDataParents` deliberately: nothing is read
    // from the workspace and no receptor is exported before the caller has
    // agreed, which is also what makes the refusal testable without a workspace.
    const dgShortfall = fepDgShortfall(compounds.length, program);
    if (dgShortfall && !acknowledgeDgShortfall) {
        return {
            error: dgShortfall,
            shortfall: {
                message: dgShortfall,
                count: compounds.length,
                program,
                needsAcknowledgement: true,
            },
        };
    }

    // All ligands (and cofactors, ions) must share one receptor.
    const { dataParentsList } = App.partitionByDataParents([...compounds, ...cofactors, ...ions]);
    if (dataParentsList.length > 1) {
        return { error: 'FEP needs all compounds (and cofactors, ions) from the same protein structure.' };
    }

    // The receptor: the selected chains (all of them when none are named)
    // plus the selected ions as HETATM records. Always the local export: the
    // worker prepares it for OpenMM afterwards, and only the local export
    // takes a chain / ion selection.
    const { pdb, error, pdbObj } = await UserActions.GetProteinPdb(
        compounds[0], { method: 'local', chains, ions },
    );
    if (error) return { error };
    if (!pdbObj || pdbObj.residues.length === 0) {
        return { error: 'No protein available for FEP receptor.' };
    }
    if (!pdb) return { error: 'No PDB data produced for FEP receptor.' };

    // SDF titles MUST be the compounds' resSpec so the cpaws/OpenFE ligand
    // names round-trip back to these compounds in integrateResults.
    const sdfOptions = {
        useSpecForName: true,
        propInclusion: 'custom',
        customPropFn: (c) => ({ bmapsResSpec: c.resSpec }),
    };
    const ligandSdf = Compound.makeSDF(compounds, sdfOptions);
    let cofactorSdf;
    if (cofactors.length > 0) {
        const built = cofactorSdfFor(cofactors, sdfOptions);
        if (built.error) return { error: built.error };
        cofactorSdf = built.sdf;
    }

    const requestData = buildFepRequest({
        pdb,
        ligandSdf,
        cofactorSdf,
        program,
        target,
        mode,
        runOptimization,
        network,
        extraSamplingResidues,
        maxRuntimeMinutes,
        jobName,
        ligandCount: compounds.length,
        receptorPrep,
        abfeWindows,
    });

    // The stash rides along with the job so results can be applied later to
    // exactly these compounds on exactly this structure (resSpecs are only
    // unique within a structure).
    const { mapCase } = App.getDataParents(compounds[0]) || {};
    const stash = {
        program,
        target,
        ligandSpecs: compounds.map((c) => c.resSpec),
        cofactorSpecs: cofactors.map((c) => c.resSpec),
        ionSpecs: ions.map((g) => g.resSpec),
        chains: chains || null,
        receptorPrep: receptorPrep || 'termini',
        structure: { name: mapCase?.displayName || null },
    };

    const response = await createJob(jobtype, requestData, stash);
    return { ...response, program };
}

/**
 * The ions / cofactors a receptor selection expects: those on a selected
 * chain, plus those not on any protein chain at all (the same rule as the
 * simulation form). Pure, so the form's defaults are testable.
 * @param {{ chain: string }[]} groups - ion or cofactor atom groups
 * @param {string[]} selectedChains - chain ids the user kept
 * @param {string[]} allProteinChains - every protein chain id of the structure
 * @returns {{ chain: string }[]}
 */
export function receptorGroupsOnChains(groups, selectedChains, allProteinChains) {
    const selected = new Set(selectedChains);
    const protein = new Set(allProteinChains);
    return (groups || []).filter((g) => selected.has(g.chain) || !protein.has(g.chain));
}

/**
 * SDF for the cofactors sent along with a campaign. A cofactor in BMaps is
 * usually a Cofactor atom group (HEM, NAD, ATP, ... — `cofactorResidueNames`)
 * that came in with the structure, which has atoms and bonds but no
 * server-provided mol block, so its block is written from the atoms; a
 * workspace Compound used as a cofactor keeps its own SDF writer. Titles are
 * resSpecs, like the ligands'.
 * @param {(import('BMapsModel').Compound|import('BMapsModel/atomgroups').AtomGroup)[]} groups
 * @param {object} sdfOptions - passed to a Compound's `getSDF`
 * @returns {{ sdf?: string, error?: string }}
 */
export function cofactorSdfFor(groups, sdfOptions={}) {
    const parts = [];
    for (const g of groups) {
        if (typeof g.getSDF === 'function') {
            parts.push(g.getSDF(sdfOptions));
            continue;
        }
        // Formal charges inferred from valence: BMaps atoms carry none, and
        // openfe (RDKit) refuses a charged atom without one (NAD's pyridinium N).
        const block = molBlockForAtoms(g.getAtoms(), g.resSpec, { formalCharges: 'infer' });
        if (!block) return { error: `Cofactor ${g.resSpec} has no atoms to write.` };
        parts.push(makeOneSDF(block, { bmapsResSpec: g.resSpec }));
    }
    return { sdf: parts.join('') };
}

/**
 * The workspace compounds a completed FEP job's ΔG may be applied to: the
 * ones it was submitted for (by resSpec), on the structure it was submitted
 * from when that is known. Older jobs without a stash fall back to every
 * loaded compound, matched by name.
 * @param {object} job - a job record carrying a `stash` (the retrieve-job-results
 *   response, or `localJobState[jobid]` after results were loaded) or the
 *   `requestmeta` the polled job list carries
 * @param {import('BMapsModel').Compound[]} [loaded] - candidate pool
 *   (default: all loaded compounds)
 * @returns {import('BMapsModel').Compound[]}
 */
export function fepCandidateCompounds(job, loaded) {
    const scope = job?.stash || FepScoringJob.scopeFromRequestMeta(job);
    return FepScoringJob.candidateCompounds(scope, loaded);
}

/**
 * Apply a completed FEP job's per-ligand ΔG onto workspace compounds.
 *
 * FEP jobs are persistent (they run for hours and live in the Free Energy
 * panel), so results are applied on demand rather than by a blocking wait.
 * Candidates default to the compounds the job was submitted for, on the
 * structure it was submitted from (see {@link fepCandidateCompounds}).
 *
 * @param {string} jobid
 * @param {object} [opts]
 * @param {import('BMapsModel').Compound[]} [opts.compounds] - restrict the set
 *   of compounds to apply scores to.
 * @returns {Promise<import('BMapsSrc/jobman/JobDefinition').IntegrateResultsReturn>}
 */
export async function applyFepScoringResults(jobid, { compounds } = {}) {
    // Without an explicit set, the job class resolves the candidates from the
    // stash that comes back with the results (the polled job list never has
    // one), so the ΔG lands on the compounds and structure it was run for.
    const items = (compounds && compounds.length) ? ensureArray(compounds) : undefined;
    return loadResult(jobid, { items, apply: true });
}

/**
 * "CHAIN:RESNUM" for a residue (the form the TMD driver takes), or null.
 * @param {import('BMapsModel').Residue} residue
 * @returns {string|null}
 */
export function residueSpecOf(residue) {
    const atom = residue?.getAtoms?.()[0];
    if (!atom || atom.resi == null) return null;
    return `${atom.chain || ''}:${atom.resi}`;
}

function compareResidueSpecs(a, b) {
    const [ca, ra] = a.split(':');
    const [cb, rb] = b.split(':');
    return ca.localeCompare(cb) || (Number(ra) - Number(rb));
}

/**
 * Residue specs ("CHAIN:RESNUM") within the binding-site distance of any of
 * the given compounds, sorted by chain then residue number. The default
 * suggestion for TMD extra-sampling residues.
 * @param {import('BMapsModel').Compound[]} compounds
 * @param {{ distance?: number }} [opts] - override the binding-site distance
 * @returns {string[]}
 */
export function bindingSiteResidueSpecs(compounds, { distance } = {}) {
    const specs = new Set();
    for (const compound of ensureArray(compounds)) {
        const { caseData } = App.getDataParents(compound) || {};
        if (!caseData) continue;
        const bindingSite = BindingSite.calculate(
            App.Workspace, { compound }, distance != null ? { bindingSiteDistance: distance } : {},
        );
        if (!bindingSite) continue;
        for (const residue of bindingSite.getComponentsInRange(caseData.getResidues())) {
            const spec = residueSpecOf(residue);
            if (spec) specs.add(spec);
        }
    }
    return [...specs].sort(compareResidueSpecs);
}

/**
 * Parse a user-typed residue list ("A:123, A:145 B:7") into specs.
 * @param {string} text
 * @returns {string[]}
 */
export function parseResidueSpecs(text) {
    return String(text || '')
        .split(/[\s,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
}

/**
 * The chain most of the compounds' binding-site residues are on (ties: the
 * first by name), or undefined: the default receptor chain for a
 * single-chain program such as GROMACS RBFE.
 * @param {import('BMapsModel').Compound[]} compounds
 * @returns {string|undefined}
 */
export function bindingChain(compounds) {
    const counts = new Map();
    for (const spec of bindingSiteResidueSpecs(compounds)) {
        const chain = spec.split(':')[0];
        counts.set(chain, (counts.get(chain) || 0) + 1);
    }
    let best;
    for (const [chain, n] of [...counts.entries()].sort(([a], [b]) => a.localeCompare(b))) {
        if (best === undefined || n > counts.get(best)) best = chain;
    }
    return best;
}
